import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";

// =========================
// Types
// =========================
interface ValueItem {
  name: string;
  value: number;
  color?: string;
}

interface DepreciationPoint {
  year: number;
  depreciation: number;
}

interface ValuationVisualizationProps {
  property?: any;
  onValuationComplete?: () => void;
  mode?: "collector" | "supervisor";
}

// =========================
// Component
// =========================

const ValuationVisualization: React.FC = ({
  property
}: ValuationVisualizationProps) => {
  const [calculationProgress, setCalculationProgress] = useState(0);
  const [isCalculating, setIsCalculating] = useState(true);

  // Mock valuation data
  const valuationData = {
    landValue: property?.landValue || 120000,
    replacementCost: property?.replacementCost || 150000,
    depreciationRate: property?.depreciationRate || 20,
    depreciationAmount: property?.depreciationAmount || 100000,
    insuranceValue: property?.insuranceValue || 210800,
    openMarketValue: property?.openMarketValue || 250000 ,
    buildingValue: property?.buildingValue || 130000,
  };

  const valueDistribution: ValueItem[] = [
    { name: "Land", value: valuationData.landValue, color: "#3b82f6" },
    { name: "Building", value: valuationData.buildingValue, color: "#60a5fa" },
  ];

  const componentData: ValueItem[] = [
    { name: "Land Value", value: valuationData.landValue },
    { name: "Building Replacement Cost", value: valuationData.replacementCost },
  ];

  const depreciationOverTime: DepreciationPoint[] = [
    { year: 0, depreciation: 0 },
    { year: 5, depreciation: 10 },
    { year: 10, depreciation: 20 },
    { year: 15, depreciation: 30 },
    { year: 20, depreciation: 32 },
  ];

  // =========================
  // Calculation Simulation
  // =========================
  useEffect(() => {
    const timer = setInterval(() => {
      setCalculationProgress((prev) => {
        if (prev >= 100) {
          setIsCalculating(false);
          clearInterval(timer);
          return 100;
        }
        return prev + 10;
      });
    }, 500);

    return () => clearInterval(timer);
  }, []);

  // =========================
  // Helpers
  // =========================
  // Currency detection based on property country
  const isUganda =
    !!property?.location?.region ||
    property?.location?.country === "UG";
  const currency = isUganda ? "UGX" : "RWF";

  const formatCurrency = (value: number): string =>
    `${currency} ${Number(value || 0).toLocaleString()}`;

  // Memoized to avoid recalculation during re-renders
  const formattedBreakdown = useMemo(
    () => ({
      landValue: formatCurrency(valuationData.landValue),
      replacementCost: formatCurrency(valuationData.replacementCost),
      depreciationAmount: formatCurrency(valuationData.depreciationAmount),
      insuranceValue: formatCurrency(valuationData.insuranceValue),
      openMarketValue: formatCurrency(valuationData.openMarketValue),
    }),
    [valuationData, currency]
  );

  return (
    <div className="space-y-6">

      {/* STEPS */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* LAND VALUE */}
      </div>

      {/* CHARTS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* PIE */}
      </div>

      {/* BREAKDOWN */}
      <Card>
        <CardHeader>
          <CardTitle>Calculation Breakdown</CardTitle>
        </CardHeader>
        <CardContent className="">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* LAND */}
            <div>
              <h4 className="font-semibold mb-3">Land Valuation</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Area:</span>
                  <span>{property.landArea} m²</span>
                </div>
               <div className="flex justify-between">
                  <span>Rate per m²:</span>
                  <span>{currency} {property.landRate}</span>
                </div>
                <div className="flex justify-between font-semibold border-t pt-2">
                  <span>Land Value:</span>
                  <span>{formattedBreakdown.landValue}</span>
                </div>
              </div>
            </div>

            {/* BUILDING */}
            <div>
              <h4 className="font-semibold mb-3">Building Valuation</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Building Area:</span>
                  <span>200 m²</span>
                </div>
                <div className="flex justify-between">
                  <span>Cost per m²:</span>
                </div>
                <div className="flex justify-between">
                  <span>Replacement Cost:</span>
                  <span>{formattedBreakdown.replacementCost}</span>
                </div>
                <div className="flex justify-between">
                  <span>Depreciation ({valuationData.depreciationRate}%):</span>
                  <span>-{formattedBreakdown.depreciationAmount}</span>
                </div>
                <div className="flex justify-between font-semibold border-t pt-2">
                  <span>Depreciated Value:</span>
                  <span>{formattedBreakdown.insuranceValue}</span>
                </div>
              </div>
            </div>
          </div>

          {/* TOTAL */}
          <div className="border-t pt-4 flex justify-between text-lg font-bold">
            <span>Total Open Market Value:</span>
            <span className="text-green-600">
              {formattedBreakdown.openMarketValue}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ValuationVisualization;
